export function esc(v) {
  return String(v ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll("\"", "&quot;");
}

export function quoteArg(v) {
  const s = String(v || "").trim();
  if (!s) return "\"\"";
  const escaped = s.replaceAll("\"", "`\"");
  return `"${escaped}"`;
}

export function downloadText(filename, text) {
  const blob = new Blob([String(text || "")], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export function formatTimestamp(value) {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString();
}

export function formatBytes(value) {
  const bytes = Number(value);
  if (!Number.isFinite(bytes) || bytes < 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let size = bytes / 1024;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  return `${size.toFixed(size >= 100 ? 0 : size >= 10 ? 1 : 2)} ${units[unitIndex]}`;
}

export function formatDistanceNm(value) {
  const nm = Number(value);
  if (!Number.isFinite(nm) || nm < 0) return "";
  return `${nm.toFixed(nm >= 100 ? 0 : nm >= 10 ? 1 : 2)} nm`;
}

export function formatSoundSpeed(value) {
  const speed = Number(value);
  if (!Number.isFinite(speed)) return "";
  return speed.toFixed(speed >= 1000 ? 1 : 2);
}

export function haversineMeters(lat1, lon1, lat2, lon2) {
  const toRad = (deg) => (Number(deg) * Math.PI) / 180;
  const phi1 = toRad(lat1);
  const phi2 = toRad(lat2);
  const dPhi = toRad(Number(lat2) - Number(lat1));
  const dLambda = toRad(Number(lon2) - Number(lon1));
  const a = Math.sin(dPhi / 2) ** 2
    + Math.cos(phi1) * Math.cos(phi2) * Math.sin(dLambda / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return 6371008.8 * c;
}

export function lineDistanceNm(coords) {
  const points = Array.isArray(coords) ? coords : [];
  let meters = 0;
  for (let i = 1; i < points.length; i += 1) {
    const prev = points[i - 1];
    const next = points[i];
    if (!Array.isArray(prev) || !Array.isArray(next)) continue;
    if (![prev[1], prev[0], next[1], next[0]].every((value) => Number.isFinite(Number(value)))) continue;
    meters += haversineMeters(prev[1], prev[0], next[1], next[0]);
  }
  return meters / 1852;
}

export function statusToneStyle(tone) {
  const key = String(tone || "").toLowerCase();
  if (key === "ok") return "background:rgba(34,197,94,.14); color:#86efac; border:1px solid rgba(34,197,94,.28);";
  if (key === "warn") return "background:rgba(250,204,21,.14); color:#fde68a; border:1px solid rgba(250,204,21,.26);";
  if (key === "fail") return "background:rgba(248,113,113,.14); color:#fca5a5; border:1px solid rgba(248,113,113,.28);";
  return "background:rgba(148,163,184,.12); color:#cbd5e1; border:1px solid rgba(148,163,184,.24);";
}

export function statusBadge(label, tone) {
  return `<span style="display:inline-flex; align-items:center; padding:2px 8px; border-radius:999px; font-size:11px; font-weight:600; ${statusToneStyle(tone)}">${esc(label)}</span>`;
}

export function stateGlyph(isOn) {
  return isOn
    ? `<span aria-label="yes" title="yes" style="color:#86efac; font-weight:700;">&#10003;</span>`
    : `<span aria-label="no" title="no" style="color:#fca5a5; font-weight:700;">&#10005;</span>`;
}

export function createEnvironmentStatus() {
  return {
    state: "idle",
    summaryTone: "neutral",
    summaryText: "Run an environment check to confirm whether the host machine has Qimera Command available and licensed.",
    checkedAt: "",
    checks: [],
    machine: null,
  };
}

export function toFeatureList(value) {
  if (Array.isArray(value)) return value.map((item) => String(item || "").trim()).filter(Boolean);
  if (typeof value === "string") {
    return value.split(/[,;\n]/).map((item) => item.trim()).filter(Boolean);
  }
  return [];
}

export function normalizeSourceScanInterval(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 30;
  return Math.max(5, Math.round(n));
}

export function createProjectId() {
  return `proj_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export function inferProjectNameFromFolder(folderPath) {
  const normalized = String(folderPath || "").trim().replace(/[\\/]+$/, "");
  if (!normalized) return "";
  const parts = normalized.split(/[\\/]/).filter(Boolean);
  return parts[parts.length - 1] || "";
}

export function joinPathParts(parentFolder, childName) {
  const parent = String(parentFolder || "").trim().replace(/[\\/]+$/, "");
  const child = String(childName || "").trim().replace(/^[\\/]+/, "");
  if (!parent) return child;
  if (!child) return parent;
  return `${parent}\\${child}`;
}

export function defaultProjectExportFolder(projectFolder) {
  return joinPathParts(String(projectFolder || "").trim(), "Export");
}

export function resolveProjectExportFolder(project, currentExportFolder, allProjects = []) {
  const activeFolder = String(project?.folder || "").trim();
  const activeDefault = defaultProjectExportFolder(activeFolder);
  const current = String(currentExportFolder || "").trim().replace(/[\\/]+$/, "");
  if (!activeFolder) return current || activeDefault;
  if (!current) return activeDefault;
  if (current.toLowerCase() === activeDefault.toLowerCase()) return activeDefault;
  const otherProjectDefaults = (Array.isArray(allProjects) ? allProjects : [])
    .map((entry) => String(entry?.folder || "").trim())
    .filter(Boolean)
    .filter((folder) => folder.toLowerCase() !== activeFolder.toLowerCase())
    .map((folder) => defaultProjectExportFolder(folder).replace(/[\\/]+$/, "").toLowerCase());
  if (otherProjectDefaults.includes(current.toLowerCase())) {
    return activeDefault;
  }
  return current;
}

export function isLikelyAbsoluteWindowsPath(value) {
  const path = String(value || "").trim();
  return /^[A-Za-z]:\\/.test(path) || /^\\\\[^\\]+\\[^\\]+/.test(path);
}

export function sanitizeExportStem(value) {
  const cleaned = String(value || "")
    .trim()
    .replace(/[<>:"/\\|?*\x00-\x1F]/g, "-")
    .replace(/\s+/g, "_")
    .replace(/-+/g, "-")
    .replace(/_+/g, "_")
    .replace(/^[-_]+|[-_]+$/g, "");
  return cleaned || "grid";
}

export function buildHostedQimeraFileUrl(filePath) {
  const value = String(filePath || "").trim();
  if (!value) return "";
  return `/api/qimera/local-file?path=${encodeURIComponent(value)}&v=${Date.now()}`;
}
